import { useId } from 'react';
import { usePublicPage } from '@/features/public-pages';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { useRenewalOffer } from '@/hooks/useRenewalOffer';
import { isViewAsTab } from '@/lib/auth/session';
import { PERMISSIONS } from '@/lib/constants/permissions';
import {
  resolveCommandCenterCtas,
  resolveCommandCenterState,
  type BuyAccess,
  type ServiceCtaSpec,
} from '../../command-center-ctas';
import {
  COMMAND_CENTER_SERVICES as S,
  LEGAL_COMMAND_CENTER_PAGE_SLUG,
  LEGAL_COMMAND_CENTER_SECTIONS as KEYS,
} from '../../constants';
import { useDataRoomEntitlement } from '../../hooks/useDataRoomEntitlement';
import { CompareBlock } from './CompareBlock';
import { ServiceCard } from './ServiceCard';
import { ServiceCta } from './ServiceCta';
import { WorkspacesStrip } from './WorkspacesStrip';
import type { CardGridContent, ComparisonContent } from './service-content';

export interface CommandCenterServicesProps {
  /** The viewer's startup, or null for an investor, partner or individual. */
  startupId: string | null;
}

/**
 * The legal services under the Legal Command Center's top panel (MYN-331,
 * FR-300, layout A): the Issue Report and Seed Financing Package side by side,
 * the workspaces, then the comparison. Text and prices come from the Due
 * Diligence page's own blocks; each call to action follows what the viewer
 * already has (`command-center-ctas.ts`). Nothing renders while the page is
 * loading or cannot be read: the top panel is the part the user came for.
 */
export function CommandCenterServices({ startupId }: CommandCenterServicesProps) {
  const headingId = useId();
  const page = usePublicPage(LEGAL_COMMAND_CENTER_PAGE_SLUG);
  const entitlement = useDataRoomEntitlement(startupId);
  const isBillingManager = usePermission(PERMISSIONS.BILLING_MANAGE);
  const { user } = useAuth();
  const buyAccess = resolveBuyAccess(startupId, isBillingManager, Boolean(user?.activeMembership));
  const renewal = useRenewalOffer(startupId, buyAccess === 'buy');

  if (!page.data) return null;
  const content = (key: string) =>
    page.data.sections.find((section) => section.key === key)?.content;
  const issue = content(KEYS.ISSUE_REPORT) as CardGridContent | undefined;
  const seed = content(KEYS.SEED_PACKAGE) as CardGridContent | undefined;
  const workspaces = content(KEYS.WORKSPACES) as CardGridContent | undefined;
  const compare = content(KEYS.COMPARE) as ComparisonContent | undefined;
  if (!issue && !seed && !workspaces && !compare) return null;

  // Nothing is offered until the startup's access is known (still loading, or
  // the read failed and the top panel offers Retry), while a buyer's renewal
  // offer loads, and never in a view-as tab, where buying is read-only.
  const undecided = isViewAsTab() || (startupId !== null && !entitlement.data) || renewal.isLoading;
  const state = undecided ? 'blocked' : resolveCommandCenterState(entitlement.data, renewal.data);
  const ctas = resolveCommandCenterCtas(state, buyAccess);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-6">
      <h2 id={headingId} className="text-h3 text-navy">
        {S.HEADING}
      </h2>
      {(issue || seed) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {issue && (
            <ServiceCard
              content={issue}
              cta={<ServiceCta spec={ctas.issue} content={issue} startupId={startupId} />}
            />
          )}
          {seed && (
            <ServiceCard
              content={seed}
              cta={<ServiceCta spec={ctas.seed} content={seed} startupId={startupId} />}
            />
          )}
        </div>
      )}
      {workspaces && (
        <WorkspacesStrip
          content={workspaces}
          cta={<ServiceCta spec={ctas.workspaces} content={workspaces} startupId={startupId} />}
        />
      )}
      {compare && ctas.showCompare && (
        <CompareBlock
          content={compare}
          leftCta={
            compare.leftCard && (
              <ServiceCta
                spec={ctas.compareLeft}
                content={compare.leftCard}
                startupId={startupId}
              />
            )
          }
          rightCta={
            compare.rightCard && (
              <ServiceCta spec={SCHEDULE} content={compare.rightCard} startupId={startupId} />
            )
          }
        />
      )}
    </section>
  );
}

const SCHEDULE: ServiceCtaSpec = { kind: 'schedule' };

/**
 * Who may start a purchase from this page, matching where the Issue Report
 * journey would take them (`issue-report-journey.ts`).
 */
function resolveBuyAccess(
  startupId: string | null,
  isBillingManager: boolean,
  hasOrganisation: boolean,
): BuyAccess {
  if (startupId) return isBillingManager ? 'buy' : 'askAdmin';
  return hasOrganisation ? 'none' : 'buy';
}
